import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import { useUserRights } from '../../context/UserRightsContext';
// Roles API goes through REACT_APP_BACKEND_URL (gateway 3002) via UserRightsContext.ROLES_API_BASE
import UserRightsDrawer from './UserRightsDrawer';

/** Spec §5 fallback if /master-roles is unreachable */
const FALLBACK_RECOMMENDED_ROLES = [
  'Tenant Administrator',
  'Integration Administrator',
  'AI Administrator',
  'AI User',
  'File Administrator',
  'Standard User',
  'Viewer',
];

const RootDiv = styled('div')({
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ContentDiv = styled('div')({
  flexGrow: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 120px)',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  margin: theme.spacing(1),
}));

const TitleBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2),
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
}));

const Title = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 600,
  '& svg': {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 'none',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#F8F8F8',
    color: '#6B0F2A',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: '0.8125rem',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(139, 159, 232, 0.08)',
  },
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: 'rgba(139, 159, 232, 0.16)',
    '&:hover': {
      backgroundColor: 'rgba(139, 159, 232, 0.24)',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottomLeftRadius: theme.shape.borderRadius * 2,
    borderBottomRightRadius: theme.shape.borderRadius * 2,
  },
  '& .MuiTablePagination-root': {
    color: theme.palette.text.secondary,
  },
}));

const TenantRoles = () => {
  const { ROLES_API_BASE, getRolesHeaders } = useUserRights();
  const [tenantRoles, setTenantRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [selectedRoleName, setSelectedRoleName] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addMode, setAddMode] = useState('recommended'); // 'recommended' | 'custom'
  const [newRoleName, setNewRoleName] = useState('');
  const [customRoleName, setCustomRoleName] = useState('');
  const [masterRoles, setMasterRoles] = useState(FALLBACK_RECOMMENDED_ROLES);
  const [loadingMasterRoles, setLoadingMasterRoles] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addError, setAddError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');

  const assignedRoleNames = useMemo(
    () =>
      new Set(
        (tenantRoles || [])
          .map((tr) => (tr.roleName || '').trim().toLowerCase())
          .filter(Boolean)
      ),
    [tenantRoles]
  );

  const availableMasterRoles = useMemo(
    () =>
      (masterRoles || []).filter(
        (name) => name && !assignedRoleNames.has(name.trim().toLowerCase())
      ),
    [masterRoles, assignedRoleNames]
  );

  const fetchMasterRoles = async () => {
    setLoadingMasterRoles(true);
    try {
      const headers = getRolesHeaders();
      const resp = await fetch(`${ROLES_API_BASE}/master-roles`, { headers });
      if (!resp.ok) throw new Error(`Failed to load recommended roles: ${resp.status}`);
      const data = await resp.json();
      const list = Array.isArray(data) ? data.filter(Boolean) : [];
      setMasterRoles(list.length > 0 ? list : FALLBACK_RECOMMENDED_ROLES);
    } catch (err) {
      console.warn('master-roles fallback:', err.message);
      setMasterRoles(FALLBACK_RECOMMENDED_ROLES);
    } finally {
      setLoadingMasterRoles(false);
    }
  };

  const fetchTenantRoles = async () => {
    if (!tenantId) {
      setError('Tenant ID not found. Please log in again.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const headers = getRolesHeaders();
      const tenantRolesResp = await fetch(
        `${ROLES_API_BASE}/tenant/tenant/${tenantId}`,
        { headers }
      );
      if (!tenantRolesResp.ok)
        throw new Error(`Failed to fetch tenant roles: ${tenantRolesResp.status}`);
      const tenantRolesData = await tenantRolesResp.json();
      const rolesResp = await fetch(`${ROLES_API_BASE}`, { headers });
      if (!rolesResp.ok) throw new Error('Failed to fetch roles');
      const rolesData = await rolesResp.json();
      const mapped = (Array.isArray(tenantRolesData) ? tenantRolesData : []).map((tr) => {
        const roleInfo = (Array.isArray(rolesData) ? rolesData : []).find(
          (r) => r.id === tr.roleId
        );
        return {
          ...tr,
          roleName: roleInfo ? roleInfo.roleName : 'Unknown Role',
        };
      });
      setTenantRoles(mapped);
    } catch (err) {
      setError(err.message);
      setTenantRoles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenantRoles();
  }, []);

  const handleOpenAddDialog = async () => {
    setAddDialogOpen(true);
    setAddMode('recommended');
    setNewRoleName('');
    setCustomRoleName('');
    setAddError(null);
    await fetchMasterRoles();
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setAddMode('recommended');
    setNewRoleName('');
    setCustomRoleName('');
    setAddError(null);
  };

  const resolveRoleNameToAdd = () => {
    if (addMode === 'custom') return (customRoleName || '').trim();
    return (newRoleName || '').trim();
  };

  const handleAddTenantRole = async (e) => {
    e.preventDefault();
    const roleName = resolveRoleNameToAdd();
    if (!roleName || !tenantId) return;
    if (assignedRoleNames.has(roleName.toLowerCase())) {
      setAddError(`"${roleName}" is already assigned to this tenant`);
      return;
    }
    setSubmitting(true);
    setAddError(null);
    try {
      const headers = getRolesHeaders();
      const resp = await fetch(`${ROLES_API_BASE}/tenant/ensure`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: Number(tenantId), roleName }),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || `Failed to add role: ${resp.status}`);
      }
      handleCloseAddDialog();
      await fetchTenantRoles();
    } catch (err) {
      setAddError(err.message || 'Failed to add role');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRowClick = (params) => {
    setSelectedRoleId(params.row.roleId);
    setSelectedRoleName(params.row.roleName || 'Unknown Role');
    setDrawerOpen(true);
  };

  const handleOpenDelete = (row) => {
    setDeleteTarget(row);
    setDeleteError(null);
  };

  const handleCloseDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.roleId || !tenantId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const headers = getRolesHeaders();
      const resp = await fetch(
        `${ROLES_API_BASE}/tenant/${encodeURIComponent(tenantId)}/${encodeURIComponent(deleteTarget.roleId)}`,
        { method: 'DELETE', headers }
      );
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || `Failed to remove role: ${resp.status}`);
      }
      setDeleteTarget(null);
      await fetchTenantRoles();
    } catch (err) {
      setDeleteError(err.message || 'Failed to remove role');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          minHeight: '50vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading tenant roles...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
        <Button startIcon={<RefreshIcon />} variant="contained" onClick={fetchTenantRoles}>
          Retry
        </Button>
      </Box>
    );
  }

  const columns = [
    { field: 'roleName', headerName: 'Role Name', flex: 1, minWidth: 180 },
    {
      field: 'actions',
      headerName: '',
      width: 72,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="error"
          aria-label={`Remove ${params.row.roleName}`}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenDelete(params.row);
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const rows = tenantRoles.map((tr, idx) => ({
    id: tr.id || `tr-${idx}`,
    roleName: tr.roleName || '',
    roleId: tr.roleId,
  }));

  return (
    <RootDiv>
      <ContentDiv>
        <StyledPaper>
          <TitleBar>
            <Title variant="h6">
              <PersonIcon fontSize="small" />
              Tenant Roles
            </Title>
            <IconButton onClick={handleOpenAddDialog} size="small" color="primary" aria-label="Add role">
              <AddIcon />
            </IconButton>
          </TitleBar>
          <Box sx={{ flexGrow: 1, width: '100%', height: '100%', overflow: 'hidden', px: 2 }}>
            <StyledDataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              pagination
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              onRowClick={handleRowClick}
              density="compact"
              autoHeight={false}
              headerHeight={48}
              rowHeight={48}
              sx={{ height: '100%' }}
              components={{
                NoRowsOverlay: () => (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      gap: 2,
                    }}
                  >
                    <Typography color="text.secondary">No tenant roles yet.</Typography>
                    <Button variant="outlined" onClick={handleOpenAddDialog}>
                      Add role
                    </Button>
                  </Box>
                ),
              }}
            />
          </Box>
        </StyledPaper>
      </ContentDiv>

      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { maxWidth: 400 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>Add role to tenant</DialogTitle>
        <form onSubmit={handleAddTenantRole}>
          <DialogContent sx={{ pt: 0.5, pb: 1 }}>
            {addError && (
              <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setAddError(null)}>
                {addError}
              </Alert>
            )}
            <FormControl component="fieldset" sx={{ mb: 0.5 }}>
              <RadioGroup
                row
                value={addMode}
                onChange={(e) => {
                  setAddMode(e.target.value);
                  setAddError(null);
                }}
              >
                <FormControlLabel
                  value="recommended"
                  control={<Radio size="small" />}
                  label="Recommended"
                  disabled={submitting}
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio size="small" />}
                  label="Custom role"
                  disabled={submitting}
                />
              </RadioGroup>
            </FormControl>

            {addMode === 'recommended' ? (
              <>
                <FormControl
                  fullWidth
                  required
                  size="small"
                  disabled={submitting || loadingMasterRoles}
                >
                  <InputLabel id="recommended-role-label">Role</InputLabel>
                  <Select
                    labelId="recommended-role-label"
                    label="Role"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    displayEmpty={false}
                  >
                    {loadingMasterRoles ? (
                      <MenuItem value="" disabled>
                        Loading roles…
                      </MenuItem>
                    ) : availableMasterRoles.length === 0 ? (
                      <MenuItem value="" disabled>
                        All recommended roles are already added
                      </MenuItem>
                    ) : (
                      availableMasterRoles.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Pick a recommended role from the access-control specification.
                </Typography>
              </>
            ) : (
              <TextField
                fullWidth
                required
                size="small"
                autoFocus
                label="Role name"
                placeholder="e.g. Project Viewer"
                value={customRoleName}
                onChange={(e) => setCustomRoleName(e.target.value)}
                disabled={submitting}
                helperText="Creates the role if needed, then assigns it to this tenant."
              />
            )}
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 1.5, pt: 0.5 }}>
            <Button onClick={handleCloseAddDialog} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                submitting ||
                (addMode === 'recommended'
                  ? loadingMasterRoles || !newRoleName.trim()
                  : !customRoleName.trim())
              }
            >
              {submitting ? 'Adding...' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Remove role from tenant?</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>
              {deleteError}
            </Alert>
          )}
          <Typography variant="body2">
            Remove <strong>{deleteTarget?.roleName}</strong> from this tenant? Users assigned this role keep
            their assignment until you change them on Users.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <UserRightsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        roleId={selectedRoleId}
        roleName={selectedRoleName}
      />
    </RootDiv>
  );
};

export default TenantRoles;
